import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, CheckCheck, Clock, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAlerts, useAcknowledgeAlert, useBulkAcknowledgeAlerts } from '@/hooks/useAlerts';
import { useCanSendCommands } from '@/hooks/useAuth';
import { formatRelativeTime } from '@/utils/formatters';
import type { Alert } from '@/types';

const alertTypeLabels: Record<string, string> = {
  temp_high: 'High Temperature',
  temp_low: 'Low Temperature',
  humidity_high: 'High Humidity',
  humidity_low: 'Low Humidity',
  pressure_change: 'Pressure Change',
  low_battery: 'Low Battery',
  motion: 'Motion Detected',
  gps_power_save: 'GPS Power Save',
  gps_no_sat: 'Unable to obtain GPS location',
};

const alertTypeColors: Record<string, string> = {
  temp_high: 'bg-red-500',
  temp_low: 'bg-blue-500',
  humidity_high: 'bg-cyan-500',
  humidity_low: 'bg-orange-500',
  pressure_change: 'bg-purple-500',
  low_battery: 'bg-yellow-500',
  motion: 'bg-green-500',
  gps_power_save: 'bg-amber-500',
  gps_no_sat: 'bg-orange-500',
};

interface AlertCardProps {
  alert: Alert;
  onAcknowledge: (alertId: string) => void;
  isAcknowledging: boolean;
  onDeviceClick: (serialNumber: string) => void;
  canAcknowledge: boolean;
}

function AlertCard({ alert, onAcknowledge, isAcknowledging, onDeviceClick, canAcknowledge }: AlertCardProps) {
  const isAcknowledged = alert.acknowledged === 'true' || alert.acknowledged === true;

  return (
    <Card className={isAcknowledged ? 'opacity-60' : ''}>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-full ${alertTypeColors[alert.type] || 'bg-gray-500'}`}>
            <AlertTriangle className="h-5 w-5 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold">
                {alertTypeLabels[alert.type] || alert.type}
              </h3>
              {isAcknowledged ? (
                <Badge variant="secondary" className="text-xs">
                  <Check className="h-3 w-3 mr-1" />
                  Acknowledged
                </Badge>
              ) : (
                <Badge variant="destructive" className="text-xs">Active</Badge>
              )}
            </div>

            <p className="text-sm text-muted-foreground mb-2">
              {alert.message || `${alertTypeLabels[alert.type]} alert triggered`}
            </p>

            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <button
                onClick={() => onDeviceClick(alert.serial_number || alert.device_uid)}
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <MapPin className="h-3 w-3" />
                {alert.serial_number || alert.device_uid}
              </button>

              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatRelativeTime(new Date(alert.created_at))}
              </span>

              {alert.value !== undefined && (
                <span>Value: {alert.value.toFixed(2)}</span>
              )}

              {alert.threshold !== undefined && (
                <span>Threshold: {alert.threshold}</span>
              )}
            </div>

            {isAcknowledged && alert.acknowledged_at && (
              <p className="text-xs text-muted-foreground mt-2">
                Acknowledged {formatRelativeTime(new Date(alert.acknowledged_at))}
                {alert.acknowledged_by && ` by ${alert.acknowledged_by}`}
              </p>
            )}
          </div>

          {!isAcknowledged && canAcknowledge && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAcknowledge(alert.alert_id)}
              disabled={isAcknowledging}
            >
              <Check className="h-4 w-4 mr-1" />
              Acknowledge
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function Alerts() {
  const navigate = useNavigate();
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const { canSend: canAcknowledgeAlerts } = useCanSendCommands();
  const { data, isLoading, error } = useAlerts({
    acknowledged: showAcknowledged ? undefined : false,
    limit: 100,
  });

  const acknowledgeMutation = useAcknowledgeAlert();
  const bulkAcknowledgeMutation = useBulkAcknowledgeAlerts();

  const handleAcknowledge = (alertId: string) => {
    acknowledgeMutation.mutate({ alertId });
  };

  const handleAcknowledgeAll = () => {
    const activeAlertIds = activeAlerts.map(alert => alert.alert_id);
    bulkAcknowledgeMutation.mutate({ alertIds: activeAlertIds });
  };

  const handleDeviceClick = (serialNumber: string) => {
    navigate(`/devices/${serialNumber}`);
  };

  const allAlerts = data?.alerts || [];
  const alerts = typeFilter === 'all'
    ? allAlerts
    : allAlerts.filter(alert => alert.type === typeFilter);
  const activeAlerts = alerts.filter(alert => alert.acknowledged === 'false' || alert.acknowledged === false);
  const activeCount = data?.active_count || 0;

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Error Loading Alerts</h2>
        <p className="text-muted-foreground">
          {error instanceof Error ? error.message : 'Failed to load alerts'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Alerts</h1>
          <p className="text-muted-foreground">
            {activeCount} active alert{activeCount !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(alertTypeLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            onClick={() => setShowAcknowledged(!showAcknowledged)}
          >
            {showAcknowledged ? 'Show Active Only' : 'Show All Alerts'}
          </Button>

          {canAcknowledgeAlerts && activeAlerts.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="default"
                  disabled={bulkAcknowledgeMutation.isPending}
                >
                  <CheckCheck className="h-4 w-4 mr-2" />
                  Acknowledge All ({activeAlerts.length})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Acknowledge All Alerts</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will acknowledge {activeAlerts.length} active alert{activeAlerts.length !== 1 ? 's' : ''}.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleAcknowledgeAll}>
                    Acknowledge All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-red-500">{activeCount}</div>
              <div className="text-sm text-muted-foreground">Active Alerts</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{data?.count || 0}</div>
              <div className="text-sm text-muted-foreground">
                {showAcknowledged ? 'Total Alerts' : 'Unacknowledged'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 bg-muted rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-3 bg-muted rounded w-1/3" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Active Alerts</h3>
            <p className="text-muted-foreground">
              {showAcknowledged
                ? 'No alerts have been recorded yet.'
                : 'All alerts have been acknowledged.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => (
            <AlertCard
              key={alert.alert_id}
              alert={alert}
              onAcknowledge={handleAcknowledge}
              isAcknowledging={acknowledgeMutation.isPending}
              onDeviceClick={handleDeviceClick}
              canAcknowledge={canAcknowledgeAlerts}
            />
          ))}
        </div>
      )}
    </div>
  );
}
